import { useEffect, useState, useRef } from 'react';
import { Box, Text, Button, Group, ScrollArea, Paper, LoadingOverlay } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { deploymentsApi } from '../lib/api';

interface LiveLogsViewerProps {
  buildId: string | null;
  isActive: boolean;
  deploymentStatus?: string;
}

export function LiveLogsViewer({ buildId, isActive, deploymentStatus }: LiveLogsViewerProps) {
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchLogs = async () => {
    if (!buildId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await deploymentsApi.getLogs(buildId);
      setLogs(response.data.logs || []);
    } catch (err) {
      setError('Failed to fetch logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if deployment status is terminal (no need to poll)
  const isTerminalStatus = (status: string | undefined) => {
    return status && ['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(status);
  };

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [logs]);

  // Start/stop polling based on isActive and deployment status
  useEffect(() => {
    if (isActive && buildId) {
      // Fetch logs immediately
      fetchLogs();
      
      // Only start polling for non-terminal statuses
      if (!isTerminalStatus(deploymentStatus)) {
        intervalRef.current = setInterval(fetchLogs, 10000);
      }
    } else {
      // Stop polling
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, buildId, deploymentStatus]);

  if (!buildId) {
    return (
      <Paper p="md" withBorder>
        <Text color="dimmed">No build ID available</Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder>
      <Box p="md" style={{ borderBottom: '1px solid #e9ecef' }}>
        <Group position="apart">
          <Text weight={600} size="sm">Live Build Logs</Text>
          <Button
            onClick={fetchLogs}
            loading={loading}
            variant="default" 
            size="xs"
          >
            Refresh
          </Button>
        </Group>
      </Box>

      <Box style={{ position: 'relative', height: '300px' }}>
        <LoadingOverlay visible={loading && logs.length === 0} />
        
        <ScrollArea
          ref={scrollAreaRef}
          style={{ height: '100%' }}
          scrollbarSize={6}
          scrollHideDelay={1500}
        >
          <Box p="md">
            {error ? (
              <Text color="red" size="sm">{error}</Text>
            ) : logs.length === 0 ? (
              <Text color="dimmed" size="sm">
                {loading ? 'Loading logs...' : 'No logs available yet'}
              </Text>
            ) : (
              <Box style={{ fontFamily: 'Monaco, Consolas, "Courier New", monospace' }}>
                {logs.map((log, index) => (
                  <Text
                    key={index}
                    size="xs"
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.4,
                      marginBottom: '2px',
                    }}
                  >
                    {log}
                  </Text>
                ))}
              </Box>
            )}
          </Box>
        </ScrollArea>
      </Box>
    </Paper>
  );
}
